import { z } from 'zod';

const shortText = (max: number) => z.string().trim().max(max).optional();

/**
 * Tenant-weite Firmen-/Belegeinstellungen (Modul settings). Firmenstammdaten laufen
 * primär über „Gesellschaften"; diese Einstellungen dienen als PDF-Fallback und tragen
 * die Login-Seiten-Texte. `name` darf leer sein (kein Firma-Tab mehr), damit sich die
 * Login-/Branding-Einstellungen unabhängig speichern lassen.
 */
export const updateCompanySettingsSchema = z.object({
  name: z.string().trim().max(200),
  street: shortText(200),
  postalCode: shortText(20),
  city: shortText(120),
  country: shortText(120),
  uid: shortText(50),
  email: z.string().trim().email().max(200).optional().or(z.literal('')),
  phone: shortText(50),
  website: shortText(200),
  iban: shortText(50),
  bic: shortText(20),
  bankName: shortText(120),
  defaultPaymentTermsDays: z.number().int().min(0).max(365).optional(),
  /** Zahlungsplan-Positionen nur in Reihenfolge fakturierbar (ADR 0077). */
  enforcePaymentScheduleOrder: z.boolean().optional(),
  // Login-Seite / Branding (linkes Panel der Anmeldung).
  loginTitle: shortText(200),
  loginSubtitle: shortText(500),
  loginBullets: z.array(z.string().trim().max(200)).max(6).optional(),
  loginPanelColor: z
    .string()
    .trim()
    .regex(/^#[0-9a-fA-F]{6}$/, 'Farbe als #rrggbb angeben')
    .optional()
    .or(z.literal('')),
  /** Anzeigegröße des App-Logos in Prozent (50–300; 100 = Standard). */
  appLogoScalePercent: z.number().int().min(50).max(300).optional(),
});
export type UpdateCompanySettingsInput = z.infer<typeof updateCompanySettingsSchema>;

/** Standard-Anzeigegröße des App-Logos in Prozent (Fallback, wenn nichts hinterlegt). */
export const DEFAULT_APP_LOGO_SCALE_PERCENT = 100;

/** Logo-Upload: Base64 (ohne Data-URL-Präfix) + MIME-Typ. Max. ~2 MB. */
export const uploadLogoSchema = z.object({
  data: z
    .string()
    .min(1)
    .max(2_800_000, 'Logo zu groß (max. ~2 MB)')
    .regex(/^[A-Za-z0-9+/=]+$/, 'Ungültige Base64-Daten'),
  mimeType: z.enum(['image/png', 'image/jpeg']),
});
export type UploadLogoInput = z.infer<typeof uploadLogoSchema>;

/** Briefpapier-Upload: A4 als PDF oder Bild (Base64, ohne Data-URL-Präfix), max. ~8 MB. */
export const uploadLetterheadSchema = z.object({
  data: z
    .string()
    .min(1)
    .max(11_000_000, 'Briefpapier zu groß (max. ~8 MB)')
    .regex(/^[A-Za-z0-9+/=]+$/, 'Ungültige Base64-Daten'),
  mimeType: z.enum(['application/pdf', 'image/png', 'image/jpeg']),
});
export type UploadLetterheadInput = z.infer<typeof uploadLetterheadSchema>;

export const companySettingsResponseSchema = z.object({
  name: z.string(),
  street: z.string().nullable(),
  postalCode: z.string().nullable(),
  city: z.string().nullable(),
  country: z.string().nullable(),
  uid: z.string().nullable(),
  email: z.string().nullable(),
  phone: z.string().nullable(),
  website: z.string().nullable(),
  iban: z.string().nullable(),
  bic: z.string().nullable(),
  bankName: z.string().nullable(),
  defaultPaymentTermsDays: z.number().int().nullable(),
  /** Zahlungsplan-Positionen nur in Reihenfolge fakturierbar (ADR 0077). */
  enforcePaymentScheduleOrder: z.boolean(),
  /** Logo als Data-URL für die Vorschau (null = keins hinterlegt). */
  logoDataUrl: z.string().nullable(),
  /** App-weites Oberflächen-Logo (Sidebar/Login) als Data-URL (null = keins). */
  appLogoDataUrl: z.string().nullable(),
  /** Anzeigegröße des App-Logos in Prozent (null = Standard 100). */
  appLogoScalePercent: z.number().int().nullable(),
  /** Briefpapier (A4-Hintergrund) als Data-URL für die Vorschau (null = keins). */
  letterheadDataUrl: z.string().nullable(),
  // Login-Seite / Branding.
  loginTitle: z.string().nullable(),
  loginSubtitle: z.string().nullable(),
  loginBullets: z.array(z.string()),
  loginPanelColor: z.string().nullable(),
  updatedAt: z.string().datetime().nullable(),
});
export type CompanySettingsResponse = z.infer<typeof companySettingsResponseSchema>;

/**
 * Öffentliche Branding-Daten für die Anmeldeseite (kein Login nötig). Nur die
 * unkritischen Anzeige-Felder des linken Login-Panels.
 */
export const publicBrandingResponseSchema = z.object({
  companyName: z.string().nullable(),
  loginTitle: z.string().nullable(),
  loginSubtitle: z.string().nullable(),
  loginBullets: z.array(z.string()),
  loginPanelColor: z.string().nullable(),
  /** App-weites Logo als Data-URL (null = keins) — für Login-Seite und App-Kopf. */
  appLogoDataUrl: z.string().nullable(),
  /** Anzeigegröße des App-Logos in Prozent (null = Standard 100). */
  appLogoScalePercent: z.number().int().nullable(),
});
export type PublicBrandingResponse = z.infer<typeof publicBrandingResponseSchema>;
